import { useState, useMemo, useRef } from 'react';
import { InputAdornment } from '@mui/material';
import { handleLiveChatInput } from 'actions/live-chat';
import Smooch from 'smooch';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectFreeFormInputPlaceholder } from 'reducers/workflows';
import {
  selectChatId,
  selectIsLiveChatInputDisabled,
  selectLiveChatIntegration,
  selectLiveChatQuery,
  selectLiveChatStatus,
  setLiveChatQuery,
} from 'reducers/live-chat';
import IntentFormWrapper from 'components/FormWrapper/IntentFormWrapper';
import { applyZopimFunction } from 'embed/zopim';
import { debounce } from 'utils/debounce';
import { TextInput } from 'components/TextInput/TextInput';
import { UploadAttachmentButton } from './UploadAttachmentButton';
import { selectWidgetInitMethod } from 'reducers/config';
import { selectIsLatestStepZendeskCsat } from 'reducers/workflows/index';
import { sendTypingIndicator } from 'components/salesforce-messaging-one-chat/messagingService';
import { CONVERSATION_CONSTANTS } from 'components/salesforce-messaging-one-chat/constants';
import { selectUserEndedChat } from 'reducers/live-chat/index';
import { RestartChatButton } from 'components/EventMessage/RestartChatButton';

const sendKustomerTypingActivity = ({
  conversationId,
  typing,
}: {
  conversationId: string;
  typing: boolean;
}) => {
  try {
    window.KustomerCore?.sendTypingActivity?.({
      conversationId,
      typing,
    });
  } catch {
    // do nothing
  }
};

const LiveChatInput = () => {
  const dispatch = useAppDispatch();
  const [isTyping, setIsTyping] = useState(false);
  const status = useAppSelector(selectLiveChatStatus);
  const liveChatIntegration = useAppSelector(selectLiveChatIntegration);
  const chatId = useAppSelector(selectChatId);
  const placeholder = useAppSelector(selectFreeFormInputPlaceholder);
  const widgetInitMethod = useAppSelector(selectWidgetInitMethod);
  const isLatestStepZendeskCsat = useAppSelector(selectIsLatestStepZendeskCsat);
  const userEndedChat = useAppSelector(selectUserEndedChat);
  const typingInterval = useRef<NodeJS.Timeout>();
  const liveChatQuery = useAppSelector(selectLiveChatQuery);
  const isLiveChatInputDisabled = useAppSelector(selectIsLiveChatInputDisabled);

  const showStartNewChatButton = status === 'ended';

  const getMaxLength = () => {
    switch (liveChatIntegration) {
      case 'kustomer':
        return 10240;
      default:
        return undefined;
    }
  };

  const getMaxFileSizeMegabytes = () => {
    switch (liveChatIntegration) {
      case 'kustomer':
        return 5 * 1000 * 1000; // they say 5MB but kustomer seems to use 5,000,000 instead of 5,242,880 bytes
      case 'salesforce':
        // Salesforce docs are outdated, the limit may be above 10 MB
        // https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=sendFile
        return 10 * 1024 * 1024; // 10 MB
      default:
        return undefined;
    }
  };

  const debouncedEndTyping = useMemo(
    () =>
      debounce(() => {
        setIsTyping(false);

        switch (liveChatIntegration) {
          case 'kustomer':
            sendKustomerTypingActivity({
              conversationId: chatId,
              typing: false,
            });
            break;
          case 'salesforce':
            clearInterval(typingInterval.current);
            sendTypingIndicator(
              chatId,
              CONVERSATION_CONSTANTS.EntryTypes.TYPING_STOPPED_INDICATOR,
            );
            break;
          case 'zendesk':
            applyZopimFunction(widgetInitMethod, 'sendTyping', false);
            break;
        }
      }, 3000),
    [chatId, liveChatIntegration, widgetInitMethod],
  );

  const startTyping = () => {
    setIsTyping(true);

    switch (liveChatIntegration) {
      case 'kustomer':
        sendKustomerTypingActivity({
          conversationId: chatId,
          typing: true,
        });
        break;
      case 'salesforce':
        // Send it right away, and then send it every 2 seconds. This is needed
        // because Salesforce automatically removes typing indicator after 3
        // seconds. It has to be 2 seconds and not 3 to avoid sending "started"
        // and "stopped" event at the same time.
        sendTypingIndicator(
          chatId,
          CONVERSATION_CONSTANTS.EntryTypes.TYPING_STARTED_INDICATOR,
        );
        clearInterval(typingInterval.current);
        typingInterval.current = setInterval(() => {
          sendTypingIndicator(
            chatId,
            CONVERSATION_CONSTANTS.EntryTypes.TYPING_STARTED_INDICATOR,
          );
        }, 2000);
        break;
      case 'zendesk':
        applyZopimFunction(widgetInitMethod, 'sendTyping', true);
        break;
    }
  };

  const handleTypingActivity = () => {
    if (!isTyping) {
      startTyping();
    }

    debouncedEndTyping();
  };

  const onSubmit = () => {
    dispatch(handleLiveChatInput());
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setLiveChatQuery(e.target.value));
    handleTypingActivity();

    // if in smooch, send start/stop typing event here
    if (liveChatIntegration === 'sunco' && status === 'active') {
      // it's possible that Smooch is not fully init'ed when the end user is typing.
      // In this case typing functions would not be defined and an error would be thrown.
      try {
        if (e.target.value !== '') {
          Smooch.startTyping();
        } else {
          Smooch.stopTyping();
        }
      } catch {}
    }
  };

  if (showStartNewChatButton) {
    return <RestartChatButton />;
  }

  return (
    <IntentFormWrapper
      formTitle=''
      isDisabled={isLiveChatInputDisabled}
      isForm
      isLastStep={false}
      noValidate
      onSubmit={() => {
        dispatch(handleLiveChatInput());
      }}
      stepIndex={-1}
    >
      <TextInput
        aria-label={placeholder}
        dir='auto'
        disableSendMessage={isLiveChatInputDisabled}
        InputProps={{
          inputProps: {
            className: 'forethought-scrollbar',
            'data-testid': 'live-chat-input',
            maxLength: getMaxLength(),
          },
          startAdornment: (
            <InputAdornment position='start'>
              <UploadAttachmentButton
                isDisabled={
                  status !== 'active' ||
                  isLatestStepZendeskCsat ||
                  userEndedChat
                }
                maxFileSize={getMaxFileSizeMegabytes()}
              />
            </InputAdornment>
          ),
        }}
        maxRows={10}
        multiline
        onChange={onChange}
        onSendMessage={onSubmit}
        placeholder={placeholder}
        sendButtonTooltipContent={
          isLatestStepZendeskCsat
            ? 'Conversation has been ended. You can rate your conversation.'
            : ''
        }
        value={liveChatQuery}
      />
    </IntentFormWrapper>
  );
};

export default LiveChatInput;
